import type { EmailOptInBreakdown } from "@/types/EmailOptInBreakdown";

/**
 * What a send IS, as far as the consumer's opt-out gate is concerned.
 *
 * - `MARKETING` — the default and the fail-closed value: a guest with
 *   `emailOptIn === false` (declined or unsubscribed) is skipped.
 * - `ORGANISATIONAL` — "organisational notice, not marketing" (programme
 *   change, cancellation, directions). Reaches opted-out guests too; only the
 *   undeliverable/suppression gates still skip. Legal basis: § 7 UWG — an
 *   organisational mail to a registered attendee is not advertising.
 *
 * Wire contract (VERTRAG_sendclass): SEND_EMAILS `optionalParams.sendClass`
 * + `sendClassAttested`; SCHEDULED workflow action `config.sendClass` +
 * `config.sendClassAttested`. ORGANISATIONAL is only valid with
 * `sendClassAttested === true`, and only for OWNER / ADMIN (event or company
 * level — the self-fetched event role already resolves both to these two
 * strings). Anything missing or unknown is MARKETING, everywhere.
 */
export type SendClass = "MARKETING" | "ORGANISATIONAL" | "STAFF_NOTICE";

/**
 * The classes an operator can PICK for a send (campaign dialog, scheduled
 * workflow). `STAFF_NOTICE` is deliberately not one of them: it is written by
 * the consumer alone, on the arrival notice to a verified staff contact
 * (W4-3 / 4e), and never travels in a job or an action config.
 */
export type PickableSendClass = Exclude<SendClass, "STAFF_NOTICE">;

export const SEND_CLASSES: readonly PickableSendClass[] = ["MARKETING", "ORGANISATIONAL"];

/**
 * Every value a `sent_emails.sendClass` column can carry. Pinned against
 * main-backend `SendClass.java` and main-consumer `send-class.ts` by
 * `__tests__/sendClassParity.test.ts` — the three enums must agree.
 */
export const SEND_CLASS_VALUES: readonly SendClass[] = [
  "MARKETING",
  "ORGANISATIONAL",
  "STAFF_NOTICE",
];

/** A row's class is the staff-notice one (arrival mail to a third party)? */
export function isStaffNotice(value: unknown): value is "STAFF_NOTICE" {
  return value === "STAFF_NOTICE";
}

/**
 * Unknown / missing ⇒ MARKETING (fail closed). STAFF_NOTICE too: it is not
 * pickable, so a stored or injected value of it degrades to MARKETING in every
 * send dialog.
 */
export function normalizeSendClass(value: unknown): PickableSendClass {
  return value === "ORGANISATIONAL" ? "ORGANISATIONAL" : "MARKETING";
}

/**
 * May this ROLE attest an organisational notice? Mirrors the backend's role
 * list (OWNER, ADMIN, EVENT-OWNER, EVENT-ADMIN): the cockpit's per-event role
 * is already the resolved one, so company and event owners/admins both arrive
 * as "OWNER"/"ADMIN". EDITOR, VIEWER and CUSTOM never may. The server
 * re-checks; this only keeps the UI honest.
 */
export function roleMayAttestSendClass(role: string | null | undefined): boolean {
  return role === "OWNER" || role === "ADMIN";
}

/**
 * Is the choice OFFERED at all? The role must be allowed AND the backend's
 * switch (`organisationalSendEnabled` on the self-fetched role, VERTRAG
 * flag_endpoint) must be `true` — missing/false/unknown hides the picker and
 * the send stays MARKETING (fail closed).
 */
export function canChooseSendClass(
  role: string | null | undefined,
  organisationalSendEnabled: boolean | null | undefined,
): boolean {
  return organisationalSendEnabled === true && roleMayAttestSendClass(role);
}

/**
 * The i18n key of the sentence the operator ticks — sent verbatim as
 * `sendClassAttestedTextKey` so the audit log records WHICH statement was
 * confirmed (the text itself can be re-worded later; the key pins it).
 */
export const SEND_CLASS_ATTESTATION_TEXT_KEY =
  "pSendEmailValidation.sendClassAttestation";

/**
 * The attestation half of the send-class keys — what a template swap, a Velo
 * patch and a re-opened editor drop. `sendClassAttestedContentHash` is the
 * backend's: it binds the tick to the template content it was given for
 * (VERTRAG_sendclass, Nachtrag 2 / H1). The cockpit never writes it, only
 * hands a stored one back unchanged (an `enabled`-only save) or drops it.
 */
export const SEND_CLASS_ATTESTATION_KEYS = [
  "sendClassAttested",
  "sendClassAttestedTextKey",
  "sendClassAttestedLocale",
  "sendClassAttestedContentHash",
] as const;

/** Every config/param key that belongs to the send class — for strippers. */
export const SEND_CLASS_KEYS = [
  "sendClass",
  ...SEND_CLASS_ATTESTATION_KEYS,
] as const;

/**
 * The config without any attestation key (the class itself stays). Returns
 * the SAME object when there is nothing to drop, so identity-based memo and
 * dirty checks do not churn.
 */
export function withoutAttestation<T extends Record<string, unknown>>(config: T): T {
  if (!SEND_CLASS_ATTESTATION_KEYS.some((k) => k in config)) return config;
  const rest: Record<string, unknown> = { ...config };
  for (const k of SEND_CLASS_ATTESTATION_KEYS) delete rest[k];
  return rest as T;
}

export interface SendClassParams {
  sendClass: PickableSendClass;
  sendClassAttested?: true;
  sendClassAttestedTextKey?: string;
  sendClassAttestedLocale?: string;
}

/**
 * The wire fields for a send. MARKETING sends `sendClass: "MARKETING"` and
 * nothing else; ORGANISATIONAL sends the attestation as `true` plus WHICH
 * sentence was ticked (`sendClassAttestedTextKey`) in which UI language
 * (`sendClassAttestedLocale`) — the backend copies both into the audit log.
 * The caller must not get here without the tick (the send buttons are locked
 * until it is set), but a missing tick degrades to MARKETING rather than to an
 * unattested ORGANISATIONAL the backend would 400.
 *
 * All of it lives INSIDE the payload the idempotency key is bound to
 * (useClientRequestId), so switching the class after a failed send mints a
 * new key instead of replaying the old job.
 */
export function sendClassParams(
  sendClass: SendClass,
  attested: boolean,
  locale: string,
): SendClassParams {
  if (sendClass === "ORGANISATIONAL" && attested) {
    return {
      sendClass: "ORGANISATIONAL",
      sendClassAttested: true,
      sendClassAttestedTextKey: SEND_CLASS_ATTESTATION_TEXT_KEY,
      sendClassAttestedLocale: locale,
    };
  }
  return { sendClass: "MARKETING" };
}

/**
 * The SCHEDULED workflow action's config fields for a class + tick. MARKETING
 * is the ABSENCE of every send-class key; ORGANISATIONAL without the tick
 * keeps only `sendClass` (the editor then asks for the tick and the save gate
 * refuses it until given).
 */
export function sendClassConfig(
  config: Record<string, unknown>,
  sendClass: SendClass,
  attested: boolean,
  locale: string,
): Record<string, unknown> {
  const rest: Record<string, unknown> = { ...config };
  for (const k of SEND_CLASS_KEYS) delete rest[k];
  if (sendClass !== "ORGANISATIONAL") return rest;
  return attested
    ? {
        ...rest,
        sendClass: "ORGANISATIONAL",
        sendClassAttested: true,
        sendClassAttestedTextKey: SEND_CLASS_ATTESTATION_TEXT_KEY,
        sendClassAttestedLocale: locale,
      }
    : { ...rest, sendClass: "ORGANISATIONAL" };
}

/**
 * How many recipients WITH an email this send will drop, per class.
 *
 * - MARKETING: `skippedWithEmail` (opted-out ∪ undeliverable, counted as a set
 *   server-side), falling back to the opted-out figure on an older backend —
 *   unchanged from before the class existed.
 * - ORGANISATIONAL: opted-out guests are NOT skipped, so only the
 *   undeliverable ones are — `undeliverableWithEmail`. A backend that does not
 *   send that field yet falls back to `skippedWithEmail` for the DISPLAY, and
 *   `exact` turns false: that figure still contains the opted-out guests who
 *   WILL receive this mail, so it must never be used to lock the send button.
 *
 * `undeliverableOnly` is the "skipped for a reason the consent block cannot
 * show" figure the dialogs print under the consent notices.
 */
export function sendClassSkipFigures(
  breakdown: EmailOptInBreakdown | null | undefined,
  sendClass: SendClass,
): { skipped: number; exact: boolean; undeliverableOnly: number } {
  // STAFF_NOTICE never reaches a send dialog (it is not pickable); should a
  // caller pass it anyway it is IGNORED here and reads as the marketing
  // arithmetic — the fail-closed figure.
  if (!breakdown) return { skipped: 0, exact: true, undeliverableOnly: 0 };
  const optedOut = breakdown.optedOutWithEmail;
  if (sendClass === "ORGANISATIONAL") {
    if (typeof breakdown.undeliverableWithEmail === "number") {
      const u = Math.max(0, breakdown.undeliverableWithEmail);
      return { skipped: u, exact: true, undeliverableOnly: u };
    }
    const fallback = breakdown.skippedWithEmail ?? 0;
    return {
      skipped: fallback,
      exact: false,
      undeliverableOnly: Math.max(0, fallback - optedOut),
    };
  }
  const skipped = breakdown.skippedWithEmail ?? optedOut;
  return {
    skipped,
    exact: true,
    undeliverableOnly: Math.max(0, skipped - optedOut),
  };
}

/**
 * Does the consent picture call for the "are you sure" step before a bulk
 * send? MARKETING: anyone opted out or without recorded consent. ORGANISATIONAL:
 * the same guests — they are exactly the ones this class now REACHES, so the
 * step names that instead.
 */
export function consentNeedsConfirm(
  breakdown: EmailOptInBreakdown | null | undefined,
): boolean {
  return (
    !!breakdown &&
    breakdown.optedOutWithEmail + breakdown.noConsentWithEmail > 0
  );
}
